import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Video, Shield, CheckCircle2, Square, Circle, AlertTriangle, Download, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '@/utils/currency';
import ClarificationDialog from './ClarificationDialog';

export default function VideoRecorder({ 
  loan, 
  lenderName, 
  borrowerName, 
  open, 
  onOpenChange, 
  onSuccess, 
  showClarificationOption = false,
  isReconfirmation = false
}) {
  const { api, user, token } = useAuth();
  const { t, formatCurrency: formatCurrencyWithLang, formatDate } = useLanguage();
  const [step, setStep] = useState('consent'); // consent | script | recording | preview | uploading
  const [comment, setComment] = useState('');
  const [consentGiven, setConsentGiven] = useState(false);
  const [consentTimestamp, setConsentTimestamp] = useState(null);
  const [stream, setStream] = useState(null);
  const [recording, setRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [maxDuration, setMaxDuration] = useState(120);
  const [showClarificationDialog, setShowClarificationDialog] = useState(false);
  const videoRef = useRef(null);
  const previewRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const currency = user?.currency || 'INR';
  const amountFormatted = formatCurrencyWithLang(loan?.principal_amount, currency);
  
  const identityStatement = t('video.record.statement_template', {
    name: user?.name,
    amount: amountFormatted,
    rate: loan?.interest_rate,
    type: loan?.interest_type?.toLowerCase(),
    duration: loan?.duration_months,
    date: formatDate(loan?.start_date)
  });

  // Fetch config for max duration
  useEffect(() => {
    if (open) {
      fetch(`${BACKEND_URL}/api/config`)
        .then(r => r.json())
        .then(d => { if (d.max_video_duration) setMaxDuration(d.max_video_duration); })
        .catch(() => {});
    }
  }, [open, BACKEND_URL]);

  const cleanup = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(t => t.stop());
      setStream(null);
    }
    if (timerRef.current) clearInterval(timerRef.current);
    if (recordedUrl) URL.revokeObjectURL(recordedUrl);
    setStep('consent');
    setConsentGiven(false);
    setConsentTimestamp(null);
    setRecording(false);
    setRecordedBlob(null);
    setRecordedUrl(null);
    setElapsed(0);
    setComment('');
    chunksRef.current = [];
  }, [stream, recordedUrl]);

  useEffect(() => {
    if (!open) cleanup();
  }, [open, cleanup]);

  const handleConsent = () => {
    setConsentGiven(true);
    setConsentTimestamp(new Date().toISOString());
    setStep('script');
  };

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true,
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      setStep('recording');
    } catch (err) {
      toast.error(t('errors.unauthorized') || 'Camera access denied. Please allow camera and microphone.');
    }
  };

  const startRecording = () => {
    if (!stream) return;
    chunksRef.current = [];
    const options = { mimeType: 'video/webm;codecs=vp9,opus' };
    let recorder;
    try {
      recorder = new MediaRecorder(stream, options);
    } catch {
      try {
        recorder = new MediaRecorder(stream, { mimeType: 'video/webm' });
      } catch {
        recorder = new MediaRecorder(stream);
      }
    }
    mediaRecorderRef.current = recorder;

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'video/webm' });
      setRecordedBlob(blob);
      const url = URL.createObjectURL(blob);
      setRecordedUrl(url);
      stream.getTracks().forEach(t => t.stop());
      setStream(null);
      setStep('preview');
    };

    recorder.start(1000);
    setRecording(true);
    setElapsed(0);

    timerRef.current = setInterval(() => {
      setElapsed(prev => {
        if (prev + 1 >= maxDuration) {
          stopRecording();
          return maxDuration;
        }
        return prev + 1;
      });
    }, 1000);
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const handleUpload = async () => {
    if (!recordedBlob) return;
    setStep('uploading');
    try {
      const formData = new FormData();
      const ext = recordedBlob.type.includes('mp4') ? 'mp4' : 'webm';
      formData.append('file', recordedBlob, `video-confirmation.${ext}`);

      const deviceInfo = `${navigator.userAgent} | ${window.screen.width}x${window.screen.height}`;
      const params = new URLSearchParams({
        identity_statement: identityStatement,
        consent_given: 'true',
        consent_timestamp: consentTimestamp,
        device_info: deviceInfo,
        comment: comment,
      });

      const endpoint = isReconfirmation 
        ? `/loans/${loan.id}/reconfirmation` 
        : `/loans/${loan.id}/video-confirmation`;

      const res = await api.post(
        `${endpoint}?${params.toString()}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }
      );

      toast.success(res.data.loan_locked ? t('loan.confirmation.locked_message') : t('video.record.success'));
      onSuccess?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('video.list.error') || 'Video upload failed');
      setStep('preview');
    }
  };

  const handleClarificationSuccess = () => {
    setShowClarificationDialog(false);
    onSuccess?.();
    onOpenChange(false);
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-xl max-h-[90vh] overflow-y-auto" data-testid="video-recorder-modal">
        <DialogHeader>
          <DialogTitle className="font-serif flex items-center gap-2">
            <Video className="w-5 h-5 text-primary" />
            {t('video.record.title')}
          </DialogTitle>
          <DialogDescription>
            {isReconfirmation ? t('loan.clarification.reconfirm_message') : (t('video.record.message') || 'Record a video to confirm the agreement terms')}
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: Consent */}
        {step === 'consent' && (
          <div className="space-y-5" data-testid="consent-step">
            <div className="bg-primary/5 border border-primary/10 p-5 space-y-3">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                <h3 className="font-semibold text-sm">{t('video.record.consent_title')}</h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {t('video.record.consent_message')}
              </p>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li>- Your face and voice will be recorded</li>
                <li>- A cryptographic hash ensures the video cannot be altered</li>
                <li>- Timestamp and device information will be captured</li>
                <li>- This video may serve as legal evidence</li>
              </ul>
            </div>
            <Button
              onClick={handleConsent}
              data-testid="consent-agree-button"
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 rounded-sm uppercase tracking-wider font-semibold text-xs"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              {t('video.record.consent_checkbox')}
            </Button>
          </div>
        )}

        {/* Step 2: Identity Script */}
        {step === 'script' && (
          <div className="space-y-5" data-testid="script-step">
            <div className="bg-accent/5 border border-accent/20 p-5 space-y-3">
              <h3 className="font-semibold text-sm">{t('video.record.identity_statement')}</h3>
              <p className="text-sm text-muted-foreground">
                {t('video.record.read_statement') || 'Please read the following statement clearly while recording:'}
              </p>
              <blockquote className="border-l-4 border-accent pl-4 py-2 text-sm font-medium italic bg-white">
                "{identityStatement}"
              </blockquote>
            </div>
            <div className="flex flex-col gap-3">
              <Button
                onClick={startCamera}
                data-testid="start-camera-button"
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 rounded-sm uppercase tracking-wider font-semibold text-xs"
              >
                <Video className="w-4 h-4 mr-2" />
                {t('video.record.start_recording')}
              </Button>
              {showClarificationOption && loan?.status !== 'CLARIFICATION_REQUESTED' && (
                <>
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-background px-2 text-muted-foreground">Or</span>
                    </div>
                  </div>
                  <Button
                    onClick={() => setShowClarificationDialog(true)}
                    data-testid="request-clarification-button"
                    variant="outline"
                    className="w-full h-12 rounded-sm uppercase tracking-wider font-semibold text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    {t('loan.clarification.request_button')}
                  </Button>
                  <p className="text-xs text-muted-foreground text-center">
                    If something is unclear in the secondary party's video, you can request clarification before recording your confirmation.
                  </p>
                </>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Recording */}
        {step === 'recording' && (
          <div className="space-y-4" data-testid="recording-step">
            <div className="relative bg-black rounded-sm overflow-hidden aspect-video">
              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                className="w-full h-full object-cover"
              />
              {recording && (
                <div className="absolute top-3 left-3 flex items-center gap-2 bg-red-600 text-white px-3 py-1 rounded-sm text-xs font-semibold">
                  <Circle className="w-2.5 h-2.5 fill-current animate-pulse" />
                  REC {formatTime(elapsed)} / {formatTime(maxDuration)}
                </div>
              )}
            </div>
            <div className="bg-muted/50 p-3 rounded-sm text-xs text-muted-foreground">
              <p className="font-medium mb-1">{t('video.record.identity_statement')}:</p>
              <p className="italic">"{identityStatement}"</p>
            </div>
            <div className="flex gap-3">
              {!recording ? (
                <Button
                  onClick={startRecording}
                  data-testid="start-recording-button"
                  className="flex-1 bg-red-600 text-white hover:bg-red-700 h-12 rounded-sm uppercase tracking-wider font-semibold text-xs"
                >
                  <Circle className="w-4 h-4 mr-2 fill-current" />
                  {t('video.record.start_recording')}
                </Button>
              ) : (
                <Button
                  onClick={stopRecording}
                  data-testid="stop-recording-button"
                  className="flex-1 bg-red-600 text-white hover:bg-red-700 h-12 rounded-sm uppercase tracking-wider font-semibold text-xs"
                >
                  <Square className="w-4 h-4 mr-2 fill-current" />
                  {t('video.record.stop_recording')} ({formatTime(maxDuration - elapsed)} {t('common.next') || 'left'})
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Step 4: Preview */}
        {step === 'preview' && (
          <div className="space-y-4" data-testid="preview-step">
            <div className="bg-black rounded-sm overflow-hidden aspect-video">
              <video
                ref={previewRef}
                src={recordedUrl}
                controls
                playsInline
                className="w-full h-full object-cover"
              />
            </div>
            <p className="text-xs text-muted-foreground text-center">
              Duration: {formatTime(elapsed)} | Size: {recordedBlob ? (recordedBlob.size / 1024 / 1024).toFixed(1) : 0} MB
            </p>
            {isReconfirmation && (
              <div className="space-y-2">
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t('payment.add.note_label')} ({t('common.optional') || 'Optional'})
                </label>
                <textarea
                  className="w-full p-3 border rounded-sm text-sm focus:ring-1 focus:ring-primary outline-none transition-all"
                  placeholder={t('payment.add.note_placeholder')}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={3}
                />
              </div>
            )}
            <div className="flex gap-3">
              <Button
                onClick={() => { cleanup(); setStep('consent'); }}
                data-testid="retake-button"
                variant="outline"
                className="flex-1 h-12 rounded-sm uppercase tracking-wider font-semibold text-xs border-primary text-primary"
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleUpload}
                data-testid="submit-video-button"
                className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 h-12 rounded-sm uppercase tracking-wider font-semibold text-xs"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                {t('video.record.upload')}
              </Button>
            </div>
          </div>
        )}

        {/* Step 5: Uploading */}
        {step === 'uploading' && (
          <div className="flex flex-col items-center justify-center py-12 space-y-4" data-testid="uploading-step">
            <div className="animate-spin w-8 h-8 border-3 border-primary border-t-transparent rounded-full" />
            <p className="text-sm font-medium">{t('common.loading')}</p>
            <p className="text-xs text-muted-foreground">{t('video.record.uploading') || 'Uploading...'}</p>
          </div>
        )}
      </DialogContent>

      {/* Clarification Dialog */}
      {showClarificationOption && (
        <ClarificationDialog
          open={showClarificationDialog}
          onOpenChange={setShowClarificationDialog}
          agreement={loan}
          onSuccess={handleClarificationSuccess}
        />
      )}
    </Dialog>
  );
}
